import { AlertTriangle, Check, Loader2, X } from "lucide-react";
import React from "react";
import { Link } from "react-router-dom";

import type { PlanReview, PlanReviewLine } from "@/api/order_check";
import type { PlanCheck } from "@/pages/order-check/v2/plan-check-state";
import { Button } from "@/components/ui/button";
import { REMAINING_EPS } from "@/lib/order-check";
import { BottomSheet } from "@/lib/scan-ui/overlays";
import { apiErrorText } from "@/lib/scan-ui/screen";
import { fmtQty } from "@/lib/scan-ui/status";
import { cn } from "@/lib/utils";
import { CharacteristicBadge } from "@/pages/order-check/v2/CharacteristicBadge";

/**
 * Значок «раскладка паллет = заказ» в шапке сборки + шторка деталей.
 *
 * Сверяются ДВА разных расхождения, и путать их нельзя:
 * - раскладка ↔ заказ (`GET …/plan-review`): живой план паллет в портале
 *   (реестр грузомест, этап 3в: бот или «Раскладка») против заказа в 1С.
 *   Разошлись — собрать заказ нельзя, пока старший не поправит заказ или
 *   раскладку.
 * - план сессии (`detail.plan_coverage`) против заказа: раскладка уже
 *   сошлась, а сессия собирает по прежней — лечится «Обновить план».
 *
 * Повод — заказ 00НФ-002118 (16.10.2026): в заказе гель с характеристикой
 * OZON FRESH, на паллете бота — тот же гель без неё. Гейт скана отбивал короб
 * «Другая партия», и до этой шторки причину никто на складе не видел.
 */

const CHIP: Record<
  Exclude<PlanCheck["kind"], "free">,
  { cls: string; icon: React.ReactElement }
> = {
  loading: { cls: "bg-white/15 text-slate-200", icon: <Loader2 className="size-2.5 animate-spin" /> },
  error: { cls: "bg-red-600 text-white", icon: <AlertTriangle className="size-2.5" /> },
  "no-plan": { cls: "bg-red-600 text-white", icon: <X className="size-2.5" /> },
  mismatch: { cls: "bg-red-600 text-white", icon: <X className="size-2.5" /> },
  stale: { cls: "bg-amber-400 text-amber-950", icon: <AlertTriangle className="size-2.5" /> },
  unloaded: { cls: "bg-amber-400 text-amber-950", icon: <AlertTriangle className="size-2.5" /> },
  ok: { cls: "bg-emerald-500 text-white", icon: <Check className="size-2.5" /> },
};

/** Полная и короткая подпись. Короткая — на самых узких ТСД (< 360 px), где
 *  полная съела бы строку контрагента целиком. */
function chipText(s: Exclude<PlanCheck, { kind: "free" }>): { full: string; short: string } {
  switch (s.kind) {
    case "loading":
      return { full: "Сверка…", short: "Сверка…" };
    case "error":
      return { full: "Сверка: ошибка", short: "ошибка" };
    case "no-plan":
      return { full: "Раскладки нет", short: "нет раскл." };
    case "mismatch":
      return {
        full: `Раскладка ≠ заказ · ${s.review.problems.length}`,
        short: `раскл. ≠ ${s.review.problems.length}`,
      };
    case "stale":
      return { full: "План устарел", short: "план устар." };
    case "unloaded":
      return { full: "План не загружен", short: "план не загр." };
    case "ok":
      return { full: "Раскладка ✓", short: "раскл. ✓" };
  }
}

export function PlanCheckChip({
  state,
  onOpen,
}: {
  state: PlanCheck;
  onOpen: () => void;
}): React.ReactElement | null {
  if (state.kind === "free") return null;
  const { cls, icon } = CHIP[state.kind];
  const { full, short } = chipText(state);
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={full}
      className={cn(
        "flex h-4 shrink-0 items-center gap-0.5 rounded px-1 text-[9px] font-bold leading-none",
        cls,
      )}
    >
      {icon}
      <span className="hidden min-[360px]:inline">{full}</span>
      <span className="min-[360px]:hidden">{short}</span>
    </button>
  );
}

function GoodsLine({
  title,
  item,
  note,
}: {
  title?: string;
  item: {
    nomenclature_article: string;
    nomenclature_name: string;
    nomenclature_ref: string;
    characteristic_ref: string;
    characteristic_name: string;
    partia_name: string;
  };
  note: string;
}): React.ReactElement {
  return (
    <li className="rounded-lg border border-rose-200 bg-rose-50 p-2">
      {title ? <div className="text-[11px] font-semibold text-slate-500">{title}</div> : null}
      <div className="flex flex-wrap items-center gap-1">
        {item.nomenclature_article ? (
          <span className="font-mono text-[14px] font-bold">{item.nomenclature_article}</span>
        ) : null}
        <CharacteristicBadge name={item.characteristic_name} characteristicRef={item.characteristic_ref} />
        {item.partia_name ? (
          <span className="rounded border bg-white px-1 font-mono text-[10px]">парт. {item.partia_name}</span>
        ) : (
          <span className="rounded border bg-white px-1 text-[10px] text-slate-400">без партии</span>
        )}
      </div>
      {/* Наименование переносится целиком — как в GoodsCard. */}
      <div className="mt-0.5 text-[11px] leading-snug text-slate-600">
        {item.nomenclature_name || item.nomenclature_ref.slice(0, 8)}
      </div>
      <div className="text-[12px] font-semibold text-rose-700">{note}</div>
    </li>
  );
}

function ReviewSection({ review }: { review: PlanReview }): React.ReactElement {
  if (!review.has_plan) {
    return (
      <div className="rounded-lg border border-rose-200 bg-rose-50 p-2 text-[12px] text-rose-800">
        Раскладки нет: в плане заказа ни одной паллеты с товаром. Собирать по плану не из чего.
      </div>
    );
  }
  if (review.in_sync) {
    return (
      <div className="flex items-center gap-1.5 rounded-lg border border-emerald-200 bg-emerald-50 p-2 text-[13px] font-semibold text-emerald-800">
        <Check className="size-4 shrink-0" /> Заказ и раскладка совпадают
      </div>
    );
  }
  // «В заказе партии нет, а в раскладке есть» — признак бэкенда
  // (`NeedRow.partia_missing`): у него это ОДНА причина, а не «не разложено»
  // + «лишнее», и счётчики такой строки сходятся — без признака её бы не было.
  const needRows = review.needs
    .map((n) => ({ n, noPartia: n.partia_missing }))
    .filter(({ n, noPartia }) => noPartia || Math.abs(n.diff) > REMAINING_EPS);
  // Паллета — по порядку в плане («Паллета 2»), как в тексте бэкенда: номер
  // (SSCC) есть не у каждой — грузоместо заводится при открытии сборки.
  const strays: { pallet: string; line: PlanReviewLine }[] = review.pallets.flatMap((p, i) =>
    p.lines.filter((l) => !l.in_order).map((line) => ({ pallet: String(i + 1), line })),
  );
  // Тексты бэкенда — по одному на ту же строку, что выше нарисована
  // структурно. Совпало число — они ничего не добавляют; не совпало — бэкенд
  // знает причину, которой экран не разбирает, и её надо показать, а не съесть.
  const unexplained = review.problems.length !== needRows.length + strays.length;
  return (
    <>
      <ul className="space-y-1.5">
        {needRows.map(({ n, noPartia }) => (
          <GoodsLine
            key={`${n.nomenclature_ref}|${n.characteristic_ref}|${n.partia_ref}`}
            item={n}
            note={
              (noPartia ? "в заказе не указана партия, а в раскладке она есть · " : "") +
              `в заказе ${fmtQty(n.qty_needed)} · разложено ${fmtQty(n.qty_planned)}`
            }
          />
        ))}
        {strays.map(({ pallet, line }) => (
          <GoodsLine
            key={`${pallet}|${line.line_number}`}
            title={`Паллета ${pallet}`}
            item={line}
            note={`${fmtQty(line.qty)} шт — нет в заказе`}
          />
        ))}
      </ul>
      {unexplained && (
        <div className="mt-2">
          <div className="text-[12px] font-semibold text-slate-500">Подробно</div>
          <ul className="mt-1 space-y-1">
            {review.problems.map((p) => (
              <li key={p} className="whitespace-pre-wrap rounded bg-rose-50 px-2 py-1 text-[12px] text-rose-900">
                {p}
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
}

export function PlanCheckSheet({
  state,
  planCoverage,
  canReload,
  reloading,
  onReload,
  onRecheck,
  planHref,
  onClose,
}: {
  state: PlanCheck;
  /** Расхождение КОПИИ плана в портале с заказом (`detail.plan_coverage`). */
  planCoverage: string[];
  /** Сессия открыта — «Обновить план» есть ВСЕГДА, а не только при
   *  непустом `planCoverage`: расхождение по партии оно не видит, а подсказка
   *  шторки велит нажать именно эту кнопку. */
  canReload: boolean;
  reloading: boolean;
  onReload: () => void;
  onRecheck: () => void;
  /** Ссылка на раздел «Раскладка» — только у кого он есть и есть право. */
  planHref: string | null;
  onClose: () => void;
}): React.ReactElement {
  // Перемещение: значок сверку раскладки не запрашивает — только план сессии.
  const transfer = state.kind === "stale" && state.review === null;
  return (
    <BottomSheet onClose={onClose} panelClassName="flex max-h-[90%] flex-col">
      <div className="flex items-center justify-between border-b p-3">
        <div className="text-[15px] font-bold">Раскладка и заказ</div>
        <button type="button" onClick={onClose} className="flex size-8 items-center justify-center" aria-label="Закрыть">
          <X className="size-4" />
        </button>
      </div>

      <div className="flex-1 space-y-3 overflow-y-auto p-3">
        {state.kind === "unloaded" && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-2 text-[13px] text-amber-900">
            <div className="font-semibold">Раскладка есть, а сборка идёт без неё</div>
            <div className="mt-0.5">
              Сборка идёт без плана: гейт скана раскладку не сверяет. Нажмите «Обновить план» —
              сборка пойдёт по раскладке.
            </div>
          </div>
        )}

        {!transfer && (
          <section>
            <div className="mb-1.5 text-[12px] font-semibold text-slate-500">Заказ ↔ раскладка</div>
            {state.kind === "loading" && (
              <div className="flex items-center gap-2 py-3 text-[13px] text-slate-500">
                <Loader2 className="size-4 animate-spin" /> Сверяю…
              </div>
            )}
            {state.kind === "error" && (
              <div className="rounded-lg border border-rose-200 bg-rose-50 p-2 text-[13px] text-rose-800">
                <div className="font-semibold">Сверить раскладку не удалось</div>
                <div className="mt-0.5 whitespace-pre-wrap">{apiErrorText(state.error, "Не удалось сверить раскладку")}</div>
                <Button variant="outline" size="sm" className="mt-2" onClick={onRecheck}>
                  Повторить
                </Button>
              </div>
            )}
            {"review" in state && state.review && <ReviewSection review={state.review} />}
            {state.kind === "free" && (
              <div className="text-[13px] text-slate-500">Раскладки нет — сборка без плана, сверять нечего.</div>
            )}
          </section>
        )}

        {planCoverage.length > 0 && (
          <section>
            <div className="mb-1.5 text-[12px] font-semibold text-slate-500">План сборки</div>
            <p className="mb-1.5 text-[12px] text-slate-600">
              План, по которому идёт сборка, расходится с заказом: добрать не даст гейт скана,
              завершить — гейт заказа.
            </p>
            <ul className="space-y-1">
              {planCoverage.map((line) => (
                <li key={line} className="rounded bg-amber-50 px-2 py-1 text-[12px] text-amber-900">
                  {line}
                </li>
              ))}
            </ul>
          </section>
        )}

        {canReload && (
          <Button variant="outline" className="h-11 w-full" disabled={reloading} onClick={onReload}>
            {reloading ? "Обновляю…" : "Обновить план"}
          </Button>
        )}

        {!transfer && (
          <p className="text-[12px] text-slate-500">
            Исправляет старший склада: правка заказа в 1С или раскладки, затем «Обновить план».
            {planHref ? (
              <>
                {" "}
                <Link to={planHref} className="font-semibold text-sky-700 underline">
                  Открыть раскладку
                </Link>
              </>
            ) : null}
          </p>
        )}
      </div>

      <div className="flex gap-2 border-t p-3">
        {/* Перемещение: значок сверку раскладки не запрашивает. */}
        {!transfer && (
          <Button variant="outline" className="h-11 flex-1" disabled={state.kind === "loading"} onClick={onRecheck}>
            Перепроверить
          </Button>
        )}
        <Button className="h-11 flex-1" onClick={onClose}>
          Закрыть
        </Button>
      </div>
    </BottomSheet>
  );
}
